import { makeAutoObservable, reaction, runInAction } from "mobx";

import NetworkManager from "../Classes/NetworkManager";

export const SyncReadState = {
  LOADING: "loading",
  LIVE: "live",
  DEGRADED: "degraded",
};

export const SyncWriteState = {
  QUEUED: "queued",
  SYNCING: "syncing",
  BLOCKED: "blocked",
  FAILED: "failed",
};

export const SyncErrorKind = {
  RETRYABLE: "retryable",
  BLOCKED: "blocked",
  FATAL: "fatal",
};

export const SyncDomainStatus = {
  OK: "ok",
  SYNCING: "syncing",
  OFFLINE_PENDING: "offline_pending",
  ATTENTION: "attention",
  ERROR: "error",
};

function clearTimer(timerId) {
  if (timerId != null) {
    clearTimeout(timerId);
  }
}

function noop() {}

function createSyncError(kind, message, extra = {}) {
  const error = new Error(message);
  error.syncErrorKind = kind;
  Object.assign(error, extra);
  return error;
}

export function createBlockedSyncError(message = "Sync blocked", extra = {}) {
  return createSyncError(SyncErrorKind.BLOCKED, message, extra);
}

export function createFatalSyncError(message = "Sync failed", extra = {}) {
  return createSyncError(SyncErrorKind.FATAL, message, extra);
}

export function classifySyncError(error) {
  const explicitKind = error?.syncErrorKind;
  if (explicitKind === SyncErrorKind.BLOCKED) {
    return SyncErrorKind.BLOCKED;
  }
  if (explicitKind === SyncErrorKind.FATAL) {
    return SyncErrorKind.FATAL;
  }
  if (explicitKind === SyncErrorKind.RETRYABLE) {
    return SyncErrorKind.RETRYABLE;
  }

  const code = String(error?.code ?? "").toLowerCase();
  if (
    [
      "permission-denied",
      "unauthenticated",
      "invalid-argument",
      "not-found",
      "failed-precondition",
    ].includes(code)
  ) {
    return SyncErrorKind.FATAL;
  }

  return SyncErrorKind.RETRYABLE;
}

function computeBackoffDelayMs(attempts) {
  const safeAttempts = Math.max(1, Number(attempts) || 1);
  return Math.min(30_000, 1_000 * 2 ** (safeAttempts - 1));
}

export class SyncDomainController {
  reads = new Map();
  writes = new Map();
  nextMutationId = 1;

  constructor(domainKey, options = {}) {
    this.domainKey = String(domainKey ?? "sync-domain");
    this.networkManager = options.networkManager ?? NetworkManager.getInstance();
    this.logPrefix = `[${this.domainKey}-sync]`;

    makeAutoObservable(
      this,
      {
        networkManager: false,
        reconnectDisposer: false,
      },
      { autoBind: true },
    );

    this.reconnectDisposer = reaction(
      () => this.isOnline,
      (isOnline, wasOnline) => {
        if (isOnline && !wasOnline) {
          this.retryFailed();
        }
      },
    );
  }

  get isOnline() {
    return !(
      this.networkManager?.isOffline === true &&
      this.networkManager?.connectivityVerified !== true
    );
  }

  get readEntries() {
    return Array.from(this.reads.values());
  }

  get writeEntries() {
    return Array.from(this.writes.values());
  }

  getStatus() {
    const hasFatalWrite = this.writeEntries.some(
      (entry) =>
        entry.state === SyncWriteState.FAILED &&
        entry.errorKind === SyncErrorKind.FATAL,
    );
    if (hasFatalWrite) {
      return SyncDomainStatus.ERROR;
    }

    const hasAttentionState =
      this.writeEntries.some(
        (entry) =>
          entry.state === SyncWriteState.BLOCKED ||
          (entry.state === SyncWriteState.FAILED &&
            entry.errorKind === SyncErrorKind.BLOCKED),
      ) ||
      this.readEntries.some((entry) => entry.state === SyncReadState.DEGRADED);
    if (hasAttentionState) {
      return SyncDomainStatus.ATTENTION;
    }

    const hasPendingWrites = this.writeEntries.some((entry) =>
      [SyncWriteState.QUEUED, SyncWriteState.FAILED].includes(entry.state),
    );
    if (!this.isOnline && hasPendingWrites) {
      return SyncDomainStatus.OFFLINE_PENDING;
    }

    const hasRetryableWriteFailure = this.writeEntries.some(
      (entry) =>
        entry.state === SyncWriteState.FAILED &&
        entry.errorKind === SyncErrorKind.RETRYABLE,
    );
    if (hasRetryableWriteFailure) {
      return SyncDomainStatus.ATTENTION;
    }

    const hasSyncingWrites =
      this.writeEntries.some((entry) =>
        [SyncWriteState.QUEUED, SyncWriteState.SYNCING].includes(entry.state),
      ) ||
      this.readEntries.some((entry) => entry.state === SyncReadState.LOADING);
    if (hasSyncingWrites) {
      return SyncDomainStatus.SYNCING;
    }

    return SyncDomainStatus.OK;
  }

  get status() {
    return this.getStatus();
  }

  registerRead(readKey, startRead) {
    const normalizedReadKey = String(readKey ?? "").trim();
    if (!normalizedReadKey || typeof startRead !== "function") {
      return Promise.resolve();
    }

    const existingEntry = this.reads.get(normalizedReadKey);
    if (existingEntry?.startRead && existingEntry.startRead !== startRead) {
      this.unregisterRead(normalizedReadKey);
    }

    const currentEntry = this.reads.get(normalizedReadKey);
    if (currentEntry?.startRead === startRead && currentEntry?.unsubscribe) {
      return Promise.resolve();
    }

    const readEntry = currentEntry ?? {
      key: normalizedReadKey,
      startRead: null,
      state: SyncReadState.LOADING,
      attempts: 0,
      lastError: null,
      retryTimeoutId: null,
      unsubscribe: null,
      startPromise: null,
    };
    readEntry.startRead = startRead;
    readEntry.state = SyncReadState.LOADING;
    readEntry.lastError = null;
    readEntry.attempts = 0;
    clearTimer(readEntry.retryTimeoutId);
    readEntry.retryTimeoutId = null;
    this.reads.set(normalizedReadKey, readEntry);

    return this.startRead(normalizedReadKey);
  }

  async startRead(readKey) {
    const readEntry = this.reads.get(readKey);
    if (!readEntry || typeof readEntry.startRead !== "function") {
      return;
    }
    if (readEntry.startPromise) {
      return readEntry.startPromise;
    }

    readEntry.state = SyncReadState.LOADING;
    readEntry.lastError = null;

    readEntry.startPromise = Promise.resolve()
      .then(() =>
        readEntry.startRead({
          onReady: () => this.markReadLive(readKey),
          onError: (error, options = {}) =>
            this.markReadError(readKey, error, options),
        }),
      )
      .then((unsubscribe) => {
        runInAction(() => {
          const latestEntry = this.reads.get(readKey);
          if (latestEntry !== readEntry) {
            if (typeof unsubscribe === "function") {
              unsubscribe();
            }
            return;
          }

          if (typeof unsubscribe === "function") {
            readEntry.unsubscribe = unsubscribe;
          }
        });
      })
      .catch((error) => {
        this.markReadError(readKey, error);
      })
      .finally(() => {
        runInAction(() => {
          const latestEntry = this.reads.get(readKey);
          if (latestEntry === readEntry) {
            readEntry.startPromise = null;
          }
        });
      });

    return readEntry.startPromise;
  }

  markReadLive(readKey) {
    const readEntry = this.reads.get(readKey);
    if (!readEntry) {
      return;
    }

    readEntry.state = SyncReadState.LIVE;
    readEntry.attempts = 0;
    readEntry.lastError = null;
    clearTimer(readEntry.retryTimeoutId);
    readEntry.retryTimeoutId = null;
  }

  markReadError(readKey, error, options = {}) {
    const readEntry = this.reads.get(readKey);
    if (!readEntry) {
      return;
    }

    readEntry.state = SyncReadState.DEGRADED;
    readEntry.lastError = error;
    readEntry.attempts += 1;
    clearTimer(readEntry.retryTimeoutId);
    readEntry.retryTimeoutId = null;

    const errorKind =
      options.errorKind != null ? options.errorKind : classifySyncError(error);
    if (errorKind === SyncErrorKind.FATAL || !this.isOnline) {
      return;
    }

    const retryDelayMs = computeBackoffDelayMs(readEntry.attempts);
    readEntry.retryTimeoutId = setTimeout(() => {
      runInAction(() => {
        const latestEntry = this.reads.get(readKey);
        if (latestEntry === readEntry) {
          readEntry.retryTimeoutId = null;
        }
      });
      this.restartRead(readKey);
    }, retryDelayMs);
  }

  restartRead(readKey) {
    const readEntry = this.reads.get(readKey);
    if (!readEntry || typeof readEntry.startRead !== "function") {
      return;
    }

    if (typeof readEntry.unsubscribe === "function") {
      readEntry.unsubscribe();
      readEntry.unsubscribe = null;
    }

    this.startRead(readKey);
  }

  unregisterRead(readKey) {
    const readEntry = this.reads.get(readKey);
    if (!readEntry) {
      return;
    }

    clearTimer(readEntry.retryTimeoutId);
    readEntry.retryTimeoutId = null;
    if (typeof readEntry.unsubscribe === "function") {
      readEntry.unsubscribe();
    }
    this.reads.delete(readKey);
  }

  enqueueMutation(config = {}) {
    const normalizedEntityKey = String(config.entityKey ?? "").trim();
    if (!normalizedEntityKey || typeof config.execute !== "function") {
      return null;
    }

    const mutation = {
      id: `${normalizedEntityKey}:${this.nextMutationId++}`,
      entityKey: normalizedEntityKey,
      execute: config.execute,
      onQueued: typeof config.onQueued === "function" ? config.onQueued : noop,
      onSuccess: typeof config.onSuccess === "function" ? config.onSuccess : noop,
      onError: typeof config.onError === "function" ? config.onError : noop,
      onBlocked: typeof config.onBlocked === "function" ? config.onBlocked : noop,
    };

    let writeEntry = this.writes.get(normalizedEntityKey);
    if (!writeEntry) {
      writeEntry = {
        entityKey: normalizedEntityKey,
        state: SyncWriteState.QUEUED,
        attempts: 0,
        errorKind: null,
        lastError: null,
        currentMutation: mutation,
        nextMutation: null,
        retryTimeoutId: null,
      };
      this.writes.set(normalizedEntityKey, writeEntry);
    } else if (writeEntry.state === SyncWriteState.SYNCING) {
      writeEntry.nextMutation = mutation;
    } else {
      clearTimer(writeEntry.retryTimeoutId);
      writeEntry.retryTimeoutId = null;
      writeEntry.currentMutation = mutation;
      writeEntry.nextMutation = null;
      writeEntry.state = SyncWriteState.QUEUED;
      writeEntry.errorKind = null;
      writeEntry.lastError = null;
      writeEntry.attempts = 0;
    }

    mutation.onQueued({
      offline: !this.isOnline,
      controller: this,
    });

    this.flush(normalizedEntityKey);
    return mutation.id;
  }

  runImmediateMutation(config = {}) {
    return new Promise((resolve, reject) => {
      this.enqueueMutation({
        ...config,
        onSuccess: (result, context) => {
          config.onSuccess?.(result, context);
          resolve(result);
        },
        onBlocked: (error, context) => {
          config.onBlocked?.(error, context);
          reject(error);
        },
        onError: (error, context) => {
          config.onError?.(error, context);
          reject(error);
        },
      });
    });
  }

  flush(entityKey = null) {
    if (!this.isOnline) {
      return;
    }

    if (entityKey != null) {
      this.flushEntry(String(entityKey));
      return;
    }

    this.writeEntries.forEach((entry) => {
      this.flushEntry(entry.entityKey);
    });
  }

  async flushEntry(entityKey) {
    const writeEntry = this.writes.get(entityKey);
    if (
      !writeEntry ||
      !this.isOnline ||
      writeEntry.state === SyncWriteState.SYNCING ||
      writeEntry.state === SyncWriteState.BLOCKED ||
      !writeEntry.currentMutation
    ) {
      return;
    }

    const mutation = writeEntry.currentMutation;
    clearTimer(writeEntry.retryTimeoutId);
    writeEntry.retryTimeoutId = null;
    writeEntry.state = SyncWriteState.SYNCING;
    writeEntry.errorKind = null;
    writeEntry.lastError = null;
    writeEntry.attempts += 1;

    try {
      const result = await mutation.execute();
      let shouldFlushNext = false;
      runInAction(() => {
        const latestEntry = this.writes.get(entityKey);
        if (latestEntry !== writeEntry) {
          mutation.onSuccess(result, {
            controller: this,
            superseded: true,
          });
          return;
        }

        if (writeEntry.nextMutation) {
          writeEntry.currentMutation = writeEntry.nextMutation;
          writeEntry.nextMutation = null;
          writeEntry.state = SyncWriteState.QUEUED;
          writeEntry.errorKind = null;
          writeEntry.lastError = null;
          writeEntry.attempts = 0;
          mutation.onSuccess(result, {
            controller: this,
            superseded: true,
          });
          shouldFlushNext = true;
          return;
        }

        this.writes.delete(entityKey);
        mutation.onSuccess(result, {
          controller: this,
          superseded: false,
        });
      });

      if (shouldFlushNext) {
        this.flushEntry(entityKey);
      }
    } catch (error) {
      let retryDelayMs = null;
      runInAction(() => {
        const latestEntry = this.writes.get(entityKey);
        if (latestEntry !== writeEntry) {
          return;
        }

        const errorKind = classifySyncError(error);
        writeEntry.lastError = error;
        writeEntry.errorKind = errorKind;

        if (errorKind === SyncErrorKind.BLOCKED) {
          writeEntry.state = SyncWriteState.BLOCKED;
          mutation.onBlocked(error, {
            controller: this,
            kind: errorKind,
          });
          return;
        }

        writeEntry.state = SyncWriteState.FAILED;
        mutation.onError(error, {
          controller: this,
          kind: errorKind,
        });

        if (errorKind === SyncErrorKind.FATAL || !this.isOnline) {
          return;
        }

        retryDelayMs = computeBackoffDelayMs(writeEntry.attempts);
        writeEntry.retryTimeoutId = setTimeout(() => {
          runInAction(() => {
            const retriedEntry = this.writes.get(entityKey);
            if (retriedEntry === writeEntry) {
              writeEntry.retryTimeoutId = null;
            }
          });
          this.flushEntry(entityKey);
        }, retryDelayMs);
      });
    }
  }

  retryFailed() {
    this.readEntries
      .filter((entry) => entry.state === SyncReadState.DEGRADED)
      .forEach((entry) => this.restartRead(entry.key));

    this.writeEntries
      .filter((entry) =>
        [SyncWriteState.QUEUED, SyncWriteState.FAILED].includes(entry.state),
      )
      .forEach((entry) => this.flushEntry(entry.entityKey));
  }

  dispose() {
    this.readEntries.forEach((entry) => {
      clearTimer(entry.retryTimeoutId);
      if (typeof entry.unsubscribe === "function") {
        entry.unsubscribe();
      }
    });
    this.reads.clear();

    this.writeEntries.forEach((entry) => {
      clearTimer(entry.retryTimeoutId);
    });
    this.writes.clear();

    if (typeof this.reconnectDisposer === "function") {
      this.reconnectDisposer();
    }
  }
}

export default SyncDomainController;
